'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getHeroHeaderSizeClasses } from '@/utils/getHeroHeaderSizeClasses';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getMainHeroVerticalSpacing } from '@/utils/getVerticalSpacing';
import { isVideoUrl } from '@/utils/isVideoUrl';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';
import VideoEmbed from './atoms/VideoEmbed';

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && 'value' in x) return x.value ?? fallback;
    return x;
}

function toBoolean(value, fallback = false) {
    if (value == null) return fallback;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
    }
    return Boolean(value);
}

function normalizeMediaUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.url ||
            input.src ||
            input.Media ||
            input.media ||
            input.value ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
}

function isSvgFileUrl(input) {
    if (!input || typeof input !== 'string') return false;
    return (
        /\.svg(?:[?#].*)?$/i.test(input) ||
        /^data:image\/svg\+xml(?:;charset=[^;,]+)?(?:;base64)?,/i.test(input)
    );
}

function parseSvgNumericDimension(value) {
    if (!value) return null;
    const raw = String(value).trim();
    if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);
    if (/^\d+(\.\d+)?px$/i.test(raw)) {
        return Number(raw.replace(/px$/i, ''));
    }
    return null;
}

function parseSvgIntrinsicDimensions(markup) {
    if (!markup || typeof markup !== 'string') return null;
    const svgTagMatch = markup.match(/<svg\b([^>]*)>/i);
    if (!svgTagMatch) return null;

    const attrs = svgTagMatch[1] || '';
    const readAttr = (name) => {
        const match = attrs.match(
            new RegExp(`\\b${name}=(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i')
        );
        return match ? match[1] || match[2] || match[3] || '' : '';
    };

    const widthAttr = parseSvgNumericDimension(readAttr('width'));
    const heightAttr = parseSvgNumericDimension(readAttr('height'));
    if (widthAttr && heightAttr) {
        return { width: widthAttr, height: heightAttr };
    }

    const viewBox = readAttr('viewBox');
    if (!viewBox) return null;
    const parts = viewBox
        .trim()
        .split(/[\s,]+/)
        .map((part) => Number(part))
        .filter((part) => Number.isFinite(part));
    if (parts.length !== 4) return null;

    const width = parts[2];
    const height = parts[3];
    if (!width || !height) return null;

    return { width, height };
}

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade-in': 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide-up': 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom-in': 'zoom',
        'zoom in': 'zoom',
    };
    return map[v] ?? 'fade';
}

function parseFieldOrderValue(value) {
    return Array.from(
        new Set(
            String(value ?? '')
                .split(',')
                .map((item) => item.trim().toLowerCase())
                .filter(Boolean)
        )
    );
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (['media', 'medias', 'image', 'images'].includes(v)) return 'medias';
    if (['cta', 'ctas', 'buttons', 'button'].includes(v)) return 'ctas';
    return 'content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 24, scale: 1.1 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 1.1 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { duration: d } },
    };
}

function getBackgroundTransitionProps(rawTransition, prefersReducedMotion) {
    if (prefersReducedMotion) {
        return {
            initial: { opacity: 1 },
            animate: { opacity: 1 },
            exit: { opacity: 1 },
            transition: { duration: 0 },
        };
    }

    const transition = String(rawTransition || 'fade')
        .toLowerCase()
        .trim();
    const timing = { duration: 0.8, ease: 'easeInOut' };

    if (transition === 'slide left') {
        return {
            initial: { x: '100%', opacity: 1 },
            animate: { x: 0, opacity: 1 },
            exit: { x: '-100%', opacity: 1 },
            transition: timing,
        };
    }
    if (transition === 'slide right') {
        return {
            initial: { x: '-100%', opacity: 1 },
            animate: { x: 0, opacity: 1 },
            exit: { x: '100%', opacity: 1 },
            transition: timing,
        };
    }
    if (transition === 'slide up') {
        return {
            initial: { y: '100%', opacity: 1 },
            animate: { y: 0, opacity: 1 },
            exit: { y: '-100%', opacity: 1 },
            transition: timing,
        };
    }
    if (transition === 'slide down') {
        return {
            initial: { y: '-100%', opacity: 1 },
            animate: { y: 0, opacity: 1 },
            exit: { y: '100%', opacity: 1 },
            transition: timing,
        };
    }
    if (transition === 'zoom') {
        return {
            initial: { opacity: 0, scale: 1.08 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 1 },
            transition: timing,
        };
    }
    if (transition === 'zoom out') {
        return {
            initial: { opacity: 0, scale: 0.92 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 1 },
            transition: timing,
        };
    }
    if (transition === 'blur') {
        return {
            initial: { opacity: 0, filter: 'blur(18px)' },
            animate: { opacity: 1, filter: 'blur(0px)' },
            exit: { opacity: 0, filter: 'blur(0px)' },
            transition: timing,
        };
    }
    if (transition === 'wipe') {
        return {
            initial: { clipPath: 'inset(0 100% 0 0)' },
            animate: { clipPath: 'inset(0 0% 0 0)' },
            exit: { opacity: 1 },
            transition: timing,
        };
    }
    if (transition === 'wipe up') {
        return {
            initial: { clipPath: 'inset(100% 0 0 0)' },
            animate: { clipPath: 'inset(0% 0 0 0)' },
            exit: { opacity: 1 },
            transition: timing,
        };
    }
    return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: timing,
    };
}

function getLeadImageVariantConfig(rawVariant) {
    const variant = String(rawVariant || 'original')
        .toLowerCase()
        .trim();

    if (variant.includes('landscape')) {
        return {
            wrapperClass: 'w-full max-w-[24rem] sm:max-w-[28rem]',
            frameClass: 'w-full overflow-hidden',
            frameStyle: { aspectRatio: '16 / 9' },
            imageClass: 'h-full w-full object-cover',
        };
    }

    if (variant.includes('portrait')) {
        return {
            wrapperClass: 'w-full max-w-[12rem] sm:max-w-[14rem]',
            frameClass: 'w-full overflow-hidden',
            frameStyle: { aspectRatio: '4 / 5' },
            imageClass: 'h-full w-full object-cover',
        };
    }

    if (variant.includes('square')) {
        return {
            wrapperClass: 'w-full max-w-[12rem] sm:max-w-[14rem]',
            frameClass: 'w-full overflow-hidden',
            frameStyle: { aspectRatio: '1 / 1' },
            imageClass: 'h-full w-full object-cover',
        };
    }

    if (variant.includes('contain')) {
        return {
            wrapperClass: 'inline-block w-fit max-w-full',
            frameClass: '',
            frameStyle: null,
            imageClass:
                'h-auto w-auto max-h-24 max-w-full object-contain sm:max-h-28',
        };
    }

    return {
        wrapperClass: 'inline-block w-fit max-w-full',
        frameClass: '',
        frameStyle: null,
        imageClass: 'h-auto w-auto max-w-full object-contain',
    };
}

/* ---------------------------- Component ---------------------------- */
export default function HeroFullBG(props) {
    const {
        Header,
        SubHeader,
        HeaderSize = 'large',
        Body,
        ContentOrder,
        LeadImageUrl,
        LeadImageAltText,
        LeadImageVariant = 'original',
        LeadImageUseSiteRadius = true,
        LeadImagePosition = 50,
        ImageUrl,
        Images = [],
        SliderTransition = props.BackgroundTransition || 'Fade',
        ImagePosition = 50,
        Video,
        CTA,
        OutlineButtonText,
        BackgroundFixed = true,
        Alignment = 'center',
        VerticalSpacing = 'medium',
        Animation = 'fade',
        AnimationGroup = 'content',
        Duration = 0.6,
    } = props;

    const ColorScheme = props.ColorScheme;
    const headerSizeClass = getHeroHeaderSizeClasses(HeaderSize, 'page');
    const subHeaderSizeClass = getHeroHeaderSizeClasses('large', 'page');

    const {
        backgroundColor,
        headerColor,
        textColor,
        thirdButton,
        secondaryButton,
        overlayColor,
        boxColor,
        boxTextColor,
        boxSecondaryButton,
        boxThirdButton,
        boxBorder,
        backgroundStyle,
        overlayStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const normalizedImageUrl = normalizeMediaUrl(ImageUrl);
    const rawLeadImageUrl = normalizeMediaUrl(LeadImageUrl);
    // LeadImageUrl is rendered with an <img>. Older editor data can contain a
    // YouTube/video URL here when the same media was selected as the hero
    // background; do not emit a broken image placeholder for that value.
    const normalizedLeadImageUrl = isVideoUrl(rawLeadImageUrl)
        ? ''
        : rawLeadImageUrl;
    const normalizedVideoUrl = normalizeMediaUrl(Video);
    const repeatableBackgroundMedia = useMemo(
        () =>
            (Array.isArray(Images) ? Images : [])
                .map((image) => ({
                    url: normalizeMediaUrl(
                        image?.ImageUrl ?? image?.imageUrl ?? image
                    ),
                    position:
                        image?.ImagePosition ??
                        image?.imagePosition ??
                        ImagePosition,
                }))
                .filter((image) => image.url),
        [Images, ImagePosition]
    );
    const repeatableVideoUrl = useMemo(
        () =>
            repeatableBackgroundMedia.find((media) => isVideoUrl(media.url))
                ?.url || '',
        [repeatableBackgroundMedia]
    );
    const repeatableBackgroundImages = useMemo(
        () =>
            repeatableBackgroundMedia.filter((media) => !isVideoUrl(media.url)),
        [repeatableBackgroundMedia]
    );
    const fallbackVideoUrl =
        !normalizedVideoUrl &&
        (repeatableVideoUrl ||
            (repeatableBackgroundImages.length === 0 &&
            isVideoUrl(normalizedImageUrl)
                ? normalizedImageUrl
                : ''));
    const videoUrl = normalizedVideoUrl || fallbackVideoUrl;
    const backgroundImages = useMemo(
        () =>
            videoUrl
                ? []
                : repeatableBackgroundImages.length > 0
                ? repeatableBackgroundImages
                : normalizedImageUrl
                ? [{ url: normalizedImageUrl, position: ImagePosition }]
                : [],
        [
            ImagePosition,
            normalizedImageUrl,
            repeatableBackgroundImages,
            videoUrl,
        ]
    );
    const [activeBackgroundIndex, setActiveBackgroundIndex] = useState(0);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const activeBackground = backgroundImages[activeBackgroundIndex] || null;
    const activeBackgroundImage = activeBackground?.url || '';
    const backgroundIsEmbed = isEmbedCode(activeBackgroundImage);
    const leadImageIsEmbed = isEmbedCode(normalizedLeadImageUrl);
    const { x: leadImagePosX, y: leadImagePosY } =
        parseImagePosition(LeadImagePosition);
    const normalizedLeadImageEmbedHtml = leadImageIsEmbed
        ? normalizeSvgEmbedMarkup(normalizedLeadImageUrl, {
              x: leadImagePosX,
              y: leadImagePosY,
              mode:
                  String(LeadImageVariant || 'original')
                      .toLowerCase()
                      .trim() === 'contain'
                      ? 'contain'
                      : 'cover',
          })
        : '';
    const leadImageVariantConfig = getLeadImageVariantConfig(LeadImageVariant);
    const leadImageUseRadius = toBoolean(LeadImageUseSiteRadius, true);
    const leadImageRadiusClass = leadImageUseRadius
        ? 'rounded-[var(--shape-radius)]'
        : '';
    const leadImageAlignmentClass =
        Alignment === 'center' || Alignment === 'bottom' || Alignment === 'down'
            ? 'mx-auto'
            : 'mx-auto md:ml-0 md:mr-auto';
    const leadImageIsSvgFile =
        !leadImageIsEmbed && isSvgFileUrl(normalizedLeadImageUrl);

    const id = useId();
    const heroClass = `hero-negative-${id.replace(/:/g, '')}`;
    const videoContainerRef = useRef(null);
    const [coverSize, setCoverSize] = useState({ width: 0, height: 0 });
    const [leadSvgDimensions, setLeadSvgDimensions] = useState(null);
    const leadSvgWrapperStyle =
        leadImageIsSvgFile && !leadImageVariantConfig.frameClass
            ? leadSvgDimensions?.width
                ? {
                      width: `${leadSvgDimensions.width}px`,
                      maxWidth: '100%',
                  }
                : {
                      width: '12rem',
                      maxWidth: '100%',
                  }
            : undefined;
    const leadSvgImageClass =
        leadImageIsSvgFile && !leadImageVariantConfig.frameClass
            ? `block h-auto w-full object-contain ${leadImageRadiusClass}`
            : `block ${leadImageVariantConfig.imageClass} ${leadImageRadiusClass}`;
    const leadImageStyle = {
        objectPosition: `${leadImagePosX}% ${leadImagePosY}%`,
    };
    const iframeClass = 'pointer-events-none absolute';
    useEffect(() => {
        setActiveBackgroundIndex((current) =>
            current < backgroundImages.length ? current : 0
        );
    }, [backgroundImages.length]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mediaQuery = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        );
        const updatePreference = (event) =>
            setPrefersReducedMotion(event.matches);
        setPrefersReducedMotion(mediaQuery.matches);
        mediaQuery.addEventListener?.('change', updatePreference);
        return () =>
            mediaQuery.removeEventListener?.('change', updatePreference);
    }, []);

    useEffect(() => {
        if (backgroundImages.length < 2) return;
        const interval = window.setInterval(() => {
            setActiveBackgroundIndex(
                (current) => (current + 1) % backgroundImages.length
            );
        }, 5000);
        return () => window.clearInterval(interval);
    }, [backgroundImages.length]);

    useEffect(() => {
        if (!videoUrl) return;
        const node = videoContainerRef.current;
        if (!node) return;

        const updateCover = () => {
            const rect = node.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            const videoAspect = 16 / 9;
            const containerAspect = rect.width / rect.height;
            let width;
            let height;
            if (containerAspect >= videoAspect) {
                width = rect.width;
                height = rect.width / videoAspect;
            } else {
                height = rect.height;
                width = rect.height * videoAspect;
            }
            setCoverSize({ width, height });
        };

        updateCover();
        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(updateCover);
            observer.observe(node);
        } else {
            window.addEventListener('resize', updateCover);
        }

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateCover);
        };
    }, [videoUrl]);

    useEffect(() => {
        if (!leadImageIsSvgFile || !normalizedLeadImageUrl) {
            setLeadSvgDimensions(null);
            return;
        }

        const controller = new AbortController();
        let isCancelled = false;

        fetch(normalizedLeadImageUrl, {
            signal: controller.signal,
        })
            .then((response) => (response.ok ? response.text() : null))
            .then((markup) => {
                if (isCancelled) return;
                setLeadSvgDimensions(parseSvgIntrinsicDimensions(markup));
            })
            .catch(() => {
                if (!isCancelled) {
                    setLeadSvgDimensions(null);
                }
            });

        return () => {
            isCancelled = true;
            controller.abort();
        };
    }, [leadImageIsSvgFile, normalizedLeadImageUrl]);

    const renderVideo = () => {
        if (!videoUrl) return null;
        const width = coverSize.width || '100%';
        const height = coverSize.height || '100%';
        return (
            <VideoEmbed
                url={videoUrl}
                playing
                muted
                loop
                controls={false}
                width={width}
                height={height}
                objectFit="cover"
                playsinline
                className={iframeClass}
                style={videoPositionStyles}
            />
        );
    };

    const renderLeadImage = () => {
        if (!normalizedLeadImageUrl) return null;

        return (
            <div
                className={`${leadImageAlignmentClass} ${leadImageVariantConfig.wrapperClass}`}
                style={leadSvgWrapperStyle}
            >
                {leadImageIsEmbed && leadImageVariantConfig.frameClass ? (
                    <div
                        className={`${leadImageVariantConfig.frameClass} ${leadImageRadiusClass}`}
                        style={leadImageVariantConfig.frameStyle}
                    >
                        <div
                            className="[&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block"
                            dangerouslySetInnerHTML={{
                                __html: normalizedLeadImageEmbedHtml,
                            }}
                        />
                    </div>
                ) : leadImageIsEmbed ? (
                    <div
                        className={`max-w-full [&>svg]:block ${
                            leadImageUseRadius
                                ? `overflow-hidden ${leadImageRadiusClass}`
                                : ''
                        }`}
                        dangerouslySetInnerHTML={{
                            __html: normalizedLeadImageEmbedHtml,
                        }}
                    />
                ) : leadImageVariantConfig.frameClass ? (
                    <div
                        className={`${leadImageVariantConfig.frameClass} ${leadImageRadiusClass}`}
                        style={leadImageVariantConfig.frameStyle}
                    >
                        <img
                            src={normalizedLeadImageUrl}
                            alt={valOf(LeadImageAltText, '')}
                            width={leadSvgDimensions?.width}
                            height={leadSvgDimensions?.height}
                            className={leadSvgImageClass}
                            style={leadImageStyle}
                        />
                    </div>
                ) : (
                    <img
                        src={normalizedLeadImageUrl}
                        alt={valOf(LeadImageAltText, '')}
                        width={leadSvgDimensions?.width}
                        height={leadSvgDimensions?.height}
                        className={leadSvgImageClass}
                        style={leadImageStyle}
                    />
                )}
            </div>
        );
    };

    const renderContentNode = (item, index) => {
        const previousItem = index > 0 ? heroContentOrder[index - 1] : null;
        const spacingClass = (() => {
            if (!previousItem) return '';
            if (item === 'leadimageurl' && previousItem === 'ctabuttons') {
                return 'mt-10 lg:mt-16';
            }
            if (item === 'leadimageurl') return 'mt-8';
            if (previousItem === 'leadimageurl') return 'mt-8';
            if (item === 'subheader' && previousItem === 'header') {
                return 'mt-2';
            }
            if (item === 'ctabuttons') return 'mt-10 lg:mt-16';
            return item === 'body' ? 'mt-6' : 'mt-4';
        })();

        if (item === 'leadimageurl') {
            return (
                <div key={item} className={spacingClass}>
                    {renderLeadImage()}
                </div>
            );
        }

        if (item === 'header') {
            return (
                <MarkDown
                    key={item}
                    variant="h1"
                    className={`${spacingClass} break-words font-display tracking-tight ${headerSizeClass} ${
                        boxColor ? boxTextColor : headerColor
                    } ${textAlign}`}
                >
                    {Header}
                </MarkDown>
            );
        }

        if (item === 'subheader') {
            return (
                <MarkDown
                    key={item}
                    variant={getChildHeadingVariant(Header, 1)}
                    className={`${spacingClass} break-words font-display tracking-tight ${subHeaderSizeClass} ${
                        boxColor ? boxTextColor : headerColor
                    } ${textAlign}`}
                >
                    {SubHeader}
                </MarkDown>
            );
        }

        if (item === 'body') {
            return (
                <MarkDown
                    key={item}
                    className={`${spacingClass} text-lg font-text leading-7 md:text-xl md:leading-8 ${
                        boxColor ? boxTextColor : textColor
                    } ${bodyWidthAlign}`}
                >
                    {Body}
                </MarkDown>
            );
        }

        if (item === 'ctabuttons' && ctaRender.hasAny) {
            return (
                <div key={item} className={spacingClass}>
                    <div className="relative z-40">{ctaRender.element}</div>
                </div>
            );
        }

        return null;
    };

    // Alignment & spacing
    let textAlign = 'text-center',
        contentWidthAlign = 'mx-auto max-w-5xl',
        bodyWidthAlign = 'mx-auto max-w-3xl',
        containerAlign = 'justify-center mx-auto',
        buttonAlign = 'justify-center',
        boxVerticalSpace = '',
        boxPadding = 'py-10 md:py-16',
        contentZIndex = 'z-40',
        boxRoundingClass = 'rounded-[min(1.5rem,var(--shape-radius))]';

    switch (Alignment) {
        case 'left':
            textAlign = 'text-center md:text-left';
            contentWidthAlign = 'max-w-3xl';
            bodyWidthAlign = 'max-w-3xl';
            containerAlign = 'justify-start';
            buttonAlign = 'justify-center md:justify-start';
            break;
        case 'right':
            textAlign = 'text-center md:text-left';
            contentWidthAlign = 'max-w-3xl ml-auto';
            bodyWidthAlign = 'max-w-3xl';
            containerAlign = 'justify-end';
            buttonAlign = 'justify-center md:justify-start';
            break;
        case 'bottom':
            textAlign = 'text-center';
            contentWidthAlign = 'mx-auto max-w-5xl';
            bodyWidthAlign = 'mx-auto max-w-3xl';
            containerAlign = 'justify-center mx-auto';
            boxVerticalSpace =
                '-mb-32 pt-32 lg:-mb-48 lg:pt-40 2xl:-mb-48 2xl:pt-52';
            boxPadding = 'md:pb-24 pb-20 pt-10 md:pt-16';
            buttonAlign = 'justify-center';
            boxRoundingClass =
                'rounded-t-[min(1.5rem,var(--shape-radius))] rounded-b-none';
            break;
        case 'down':
            textAlign = 'text-center';
            contentWidthAlign = 'mx-auto max-w-5xl';
            bodyWidthAlign = 'mx-auto max-w-3xl';
            containerAlign = 'justify-center mx-auto';
            boxVerticalSpace =
                '-mb-48 pt-32 lg:-mb-80 lg:pt-40 2xl:-mb-80 2xl:pt-52';
            boxPadding = 'md:pb-24 pb-20 pt-10 md:pt-16';
            buttonAlign = 'justify-center';
            break;
        default:
            break;
    }

    const verticalRaw = valOf(VerticalSpacing, 'medium');
    const verticalKey = String(verticalRaw || 'medium').toLowerCase();
    const heroSpace = getMainHeroVerticalSpacing(verticalKey);
    const noBottomKeys = new Set(['no-bottom', 'none', 'large-no-bottom']);
    const isNoBottomSpacing = noBottomKeys.has(verticalKey);
    const shouldDockBoxToBottom = Boolean(boxColor && isNoBottomSpacing);
    const vSpace = noBottomKeys.has(verticalKey)
        ? `${heroSpace.topClass} pb-0`
        : `${heroSpace.className}`;
    if (shouldDockBoxToBottom) {
        boxRoundingClass =
            'rounded-t-[min(1.5rem,var(--shape-radius))] rounded-b-none';
    }

    // Animation
    const animationRaw = valOf(Animation, 'fade');
    const groupRaw = valOf(AnimationGroup, 'content');
    const preset = normalizeAnimation(animationRaw);
    const group = mapGroup(groupRaw);
    const enableAnim = preset !== 'none';
    const animateContent = group === 'content';
    const animateMedia = group === 'medias';
    const animateCTAs = group === 'ctas';
    const variants = getElementVariants(preset, Duration);
    const baseKey = `hfull-${preset}-${group}-${Duration}`;
    const motionProps = enableAnim
        ? {
              initial: 'offscreen',
              animate: 'onscreen',
              whileInView: 'onscreen',
              viewport: { once: false, amount: 0.3 },
              variants,
          }
        : {};

    const hasBackgroundSlider = backgroundImages.length > 1;
    const shouldFixBackground = !(
        BackgroundFixed === false ||
        BackgroundFixed === 'false' ||
        animateMedia ||
        hasBackgroundSlider
    );
    const fixedClass = shouldFixBackground
        ? 'bg-scroll lg:bg-fixed'
        : 'bg-scroll';
    const embedPositionClass = shouldFixBackground
        ? 'absolute inset-0 lg:fixed lg:inset-0'
        : 'absolute inset-0';
    const ctaRender = getCTA(props, {
        secondaryVariant: boxColor ? boxSecondaryButton : secondaryButton,
        outlineVariant: boxColor ? boxThirdButton : thirdButton,
        buttonAlign,
        wrapSpacing: '',
        buttonsClassName: 'flex flex-wrap items-center gap-2',
        enableAnim: enableAnim && animateCTAs,
        motionProps: animateCTAs ? motionProps : {},
    });
    const heroContentOrder = (() => {
        const available = [];
        if (normalizedLeadImageUrl) available.push('leadimageurl');
        if (Header) available.push('header');
        if (SubHeader) available.push('subheader');
        if (Body) available.push('body');
        if (ctaRender.hasAny) available.push('ctabuttons');

        const requested = parseFieldOrderValue(ContentOrder).filter((item) =>
            available.includes(item)
        );
        const remaining = available.filter((item) => !requested.includes(item));
        return [...requested, ...remaining];
    })();

    const { x: mediaPosX, y: mediaPosY } = parseImagePosition(
        activeBackground?.position ?? ImagePosition
    );
    const imageBackgroundStyles = {};
    if (activeBackgroundImage && !videoUrl && !backgroundIsEmbed) {
        imageBackgroundStyles.backgroundPosition = `${mediaPosX}% ${mediaPosY}%`;
    }
    const normalizedEmbedHtml = normalizeSvgEmbedMarkup(activeBackgroundImage, {
        x: mediaPosX,
        y: mediaPosY,
    });
    const backgroundTransitionProps = getBackgroundTransitionProps(
        SliderTransition,
        prefersReducedMotion
    );
    const videoPositionStyles = {
        left: `${mediaPosX}%`,
        top: `${mediaPosY}%`,
        transform: `translate(-${mediaPosX}%, -${mediaPosY}%)`,
    };

    return (
        <section
            id="top"
            data-hero
            className={`editable-component relative h-auto w-full overflow-x-hidden ${backgroundColor} ${heroClass}`}
            style={{ ...backgroundStyle, marginTop: '-172px' }}
        >
            {/* local override for ≥768px */}
            <style>{`
        @media (min-width: 768px) {
          .${heroClass} { margin-top: -70px; }
        }
      `}</style>

            {/* Background layer (video or image) */}
            <div
                ref={videoContainerRef}
                className="absolute inset-0 z-0 h-full overflow-hidden"
            >
                {(() => {
                    const videoElement = renderVideo();
                    if (videoElement) {
                        const videoNode = (
                            <div className="absolute inset-0 h-full w-full">
                                {videoElement}
                            </div>
                        );
                        return animateMedia ? (
                            <motion.div
                                key={`${baseKey}-media`}
                                className="absolute inset-0 h-full w-full"
                                {...motionProps}
                            >
                                {videoNode}
                            </motion.div>
                        ) : (
                            videoNode
                        );
                    }
                    if (!activeBackgroundImage) return null;
                    const imageNode = backgroundIsEmbed ? (
                        <div className="pointer-events-none absolute inset-0 h-full w-full overflow-hidden">
                            <div
                                className={`${embedPositionClass} [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block`}
                                dangerouslySetInnerHTML={{
                                    __html: normalizedEmbedHtml,
                                }}
                            />
                        </div>
                    ) : (
                        <div
                            className={`absolute inset-0 h-full w-full bg-cover bg-no-repeat ${fixedClass}`}
                            style={{
                                backgroundImage: `url(${activeBackgroundImage})`,
                                ...imageBackgroundStyles,
                            }}
                        />
                    );
                    const sliderNode = (
                        <AnimatePresence initial={false}>
                            <motion.div
                                key={`${activeBackgroundIndex}-${activeBackgroundImage}`}
                                className="absolute inset-0 h-full w-full origin-center"
                                {...(backgroundImages.length > 1
                                    ? backgroundTransitionProps
                                    : {})}
                            >
                                {imageNode}
                            </motion.div>
                        </AnimatePresence>
                    );
                    return animateMedia ? (
                        <motion.div
                            key={`${baseKey}-media`}
                            className="absolute inset-0 h-full w-full"
                            {...motionProps}
                        >
                            {sliderNode}
                        </motion.div>
                    ) : (
                        sliderNode
                    );
                })()}
                <div
                    className={`absolute inset-0 ${overlayColor}`}
                    style={overlayStyle}
                />
            </div>

            {/* Foreground content */}
            <div
                className={`relative ${boxVerticalSpace} ${contentZIndex} flex w-full max-w-screen-2xl ${
                    shouldDockBoxToBottom ? 'items-end' : 'items-center'
                } lg:mx-auto ${containerAlign} ${vSpace}`}
            >
                <div
                    className={`flex min-h-[500px] w-full ${
                        shouldDockBoxToBottom ? 'items-end' : 'items-center'
                    } sm:min-h-[600px] lg:min-h-[700px]`}
                >
                    <motion.div
                        key={`${baseKey}-content`}
                        className={`${textAlign} ${contentWidthAlign} flex h-full w-full flex-col ${
                            shouldDockBoxToBottom
                                ? 'justify-end'
                                : 'justify-center'
                        }`}
                        {...(animateContent ? motionProps : {})}
                    >
                        <div className={`px-6 lg:px-8`}>
                            {(CTA ||
                                OutlineButtonText ||
                                Header ||
                                SubHeader ||
                                Body) && (
                                <div
                                    className={`${boxColor} ${boxBorder} ${boxRoundingClass} ${boxPadding} md:px-10 lg:px-8`}
                                >
                                    <div className="mx-4 flex flex-col flex-wrap px-2">
                                        {heroContentOrder.map((item, index) =>
                                            renderContentNode(item, index)
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
